import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, Eye, Check, X, RefreshCw,
  Users, Clock, Calendar, UserCheck, Star, ArrowUpDown,
  CheckSquare, Square, Trash2, Download, ChevronDown, AlertTriangle
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import StarRating from '../../components/StarRating';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'phone_screen', label: 'Phone Screen Invited' },
  { value: 'phone_scheduled', label: 'Phone Scheduled' },
  { value: 'phone_completed', label: 'Phone Passed' },
  { value: 'in_person_invited', label: 'In-Person Invited' },
  { value: 'in_person_scheduled', label: 'In-Person Scheduled' },
  { value: 'interview_sent', label: 'Interview Sent' },
  { value: 'interview_confirmed', label: 'Interview Confirmed' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'hired', label: 'Hired' },
  { value: 'not_moving_forward', label: 'Not Moving Forward' },
];

const RATING_OPTIONS = [
  { value: 'all', label: 'All Ratings' },
  { value: '5', label: '5 Stars' },
  { value: '4-5', label: '4-5 Stars' },
  { value: '3-5', label: '3+ Stars' },
  { value: '1-2', label: '1-2 Stars' },
  { value: 'unrated', label: 'Unrated' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First' },
  { value: 'rating_desc', label: 'Highest Rating' },
  { value: 'rating_asc', label: 'Lowest Rating' },
  { value: 'score_desc', label: 'Highest AI Score' },
  { value: 'score_asc', label: 'Lowest AI Score' },
];

const MILEAGE_FLAG_OPTIONS = [
  { value: 'all', label: 'All Candidates' },
  { value: 'any', label: 'Any Mileage Flag' },
  { value: 'concern', label: 'Mileage Policy Concern' },
  { value: 'question', label: 'Mileage Policy Question' },
];

const STATUS_COLORS = {
  in_progress: 'bg-gray-100 text-gray-800',
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  declined: 'bg-red-100 text-red-800',
  phone_screen: 'bg-violet-100 text-violet-800',
  phone_scheduled: 'bg-purple-100 text-purple-800',
  phone_completed: 'bg-indigo-100 text-indigo-800',
  in_person_invited: 'bg-sky-100 text-sky-800',
  in_person_scheduled: 'bg-cyan-100 text-cyan-800',
  interview_sent: 'bg-purple-100 text-purple-800',
  interview_confirmed: 'bg-indigo-100 text-indigo-800',
  interviewed: 'bg-teal-100 text-teal-800',
  hired: 'bg-green-100 text-green-800',
  not_moving_forward: 'bg-gray-100 text-gray-800',
};

const BULK_STATUS_OPTIONS = [
  { value: 'approved', label: 'Approve' },
  { value: 'declined', label: 'Decline' },
  { value: 'not_moving_forward', label: 'Not Moving Forward' },
  { value: 'pending_review', label: 'Pending Review' },
];

const CandidatesPage = () => {
  const { isOwner } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [approveCandidate, setApproveCandidate] = useState(null);
  
  const statusFilter = searchParams.get('status') || 'all';
  const jobFilter = searchParams.get('job_id') || 'all';
  const ratingFilter = searchParams.get('rating') || 'all';
  const mileageFilter = searchParams.get('mileage_flag') || 'all';
  const sortBy = searchParams.get('sort') || 'newest';

  useEffect(() => {
    fetchData();
  }, [statusFilter, jobFilter, ratingFilter, mileageFilter, sortBy]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (jobFilter !== 'all') params.job_id = jobFilter;
      if (sortBy !== 'newest') params.sort_by = sortBy;
      if (mileageFilter !== 'all') params.mileage_flag = mileageFilter;
      
      if (ratingFilter === '5') {
        params.min_rating = 5;
        params.max_rating = 5;
      } else if (ratingFilter === '4-5') {
        params.min_rating = 4;
      } else if (ratingFilter === '3-5') {
        params.min_rating = 3;
      } else if (ratingFilter === '1-2') {
        params.max_rating = 2;
        params.min_rating = 1;
      }
      
      const [candidatesRes, jobsRes] = await Promise.all([
        axios.get(`${API}/candidates`, { params }),
        axios.get(`${API}/jobs`)
      ]);
      
      let candidatesList = candidatesRes.data;
      
      if (ratingFilter === 'unrated') {
        candidatesList = candidatesList.filter(c => !c.owner_rating);
      }
      
      setCandidates(candidatesList);
      setJobs(jobsRes.data);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (candidateId, newStatus) => {
    try {
      await axios.put(`${API}/candidates/${candidateId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const updateRating = async (candidateId, rating) => {
    try {
      if (rating === 0) {
        await axios.delete(`${API}/candidates/${candidateId}/rating`);
      } else {
        await axios.put(`${API}/candidates/${candidateId}/rating`, { rating });
      }
      setCandidates(prev => prev.map(c => 
        c.id === candidateId ? { ...c, owner_rating: rating || null } : c
      ));
    } catch (error) {
      toast.error('Failed to update rating');
    }
  };

  // Bulk actions
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredCandidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCandidates.map(c => c.id)));
    }
  };

  const bulkUpdateStatus = async (status) => {
    if (selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/candidates/bulk/status`, {
        candidate_ids: Array.from(selectedIds),
        status
      });
      toast.success(`${selectedIds.size} candidate(s) updated to ${status.replace(/_/g, ' ')}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} candidate(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/candidates/bulk/delete`, {
        candidate_ids: Array.from(selectedIds)
      });
      toast.success(`${selectedIds.size} candidate(s) deleted`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const exportCsv = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await axios.get(`${API}/candidates/export/csv`, {
        params,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `candidates_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const getScoreBadge = (score) => {
    if (!score) return <Badge className="bg-gray-100 text-gray-600">--</Badge>;
    if (score >= 75) return <Badge className="bg-green-100 text-green-800 border border-green-300">{score}</Badge>;
    if (score >= 50) return <Badge className="bg-yellow-100 text-yellow-800 border border-yellow-300">{score}</Badge>;
    return <Badge className="bg-red-100 text-red-800 border border-red-300">{score}</Badge>;
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-xs text-[#999999]">--</span>;
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star 
            key={i} 
            className={`h-4 w-4 ${i <= rating ? 'fill-[#E8C547] text-[#E8C547]' : 'text-gray-300'}`} 
          />
        ))}
      </div>
    );
  };

  const filteredCandidates = candidates.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const name = c.parsed_resume?.name?.toLowerCase() || '';
    const email = c.parsed_resume?.email?.toLowerCase() || '';
    const contactName = c.contact_info ? `${c.contact_info.first_name || ''} ${c.contact_info.last_name || ''}`.toLowerCase() : '';
    const contactEmail = c.contact_info?.email?.toLowerCase() || '';
    return name.includes(search) || email.includes(search) || contactName.includes(search) || contactEmail.includes(search);
  });

  const handleFilterChange = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value === 'all' || value === 'newest') {
      params.delete(key);
    } else {
      params.set(key, value);
    }
    setSearchParams(params);
  };

  const allSelected = filteredCandidates.length > 0 && selectedIds.size === filteredCandidates.length;

  return (
    <>
    <div className="p-6 space-y-6" data-testid="candidates-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">Candidate Pipeline</h1>
          <p className="text-[#666666] mt-1">Manage and review all applicants</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={exportCsv} variant="outline" className="border-[#4B2D6E] text-[#4B2D6E]" data-testid="export-csv-btn">
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
          <Button onClick={fetchData} variant="outline" className="border-[#4B2D6E] text-[#4B2D6E]">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {isOwner && selectedIds.size > 0 && (
        <Card className="bg-[#4B2D6E] text-white border-none" data-testid="bulk-action-bar">
          <CardContent className="py-3 flex flex-wrap items-center gap-3">
            <span className="font-medium text-sm">
              {selectedIds.size} selected
            </span>
            <div className="h-5 w-px bg-white/30" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="secondary" className="bg-white/20 hover:bg-white/30 text-white border-none" data-testid="bulk-status-dropdown" disabled={bulkLoading}>
                  Change Status
                  <ChevronDown className="ml-1 h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {BULK_STATUS_OPTIONS.map(opt => (
                  <DropdownMenuItem key={opt.value} onClick={() => bulkUpdateStatus(opt.value)} data-testid={`bulk-status-${opt.value}`}>
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="destructive" onClick={bulkDelete} disabled={bulkLoading} data-testid="bulk-delete-btn">
              <Trash2 className="mr-1 h-3 w-3" />
              Delete
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={() => setSelectedIds(new Set())} data-testid="bulk-clear-btn">
              Clear
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(v) => handleFilterChange('status', v)}>
              <SelectTrigger className="w-full lg:w-44" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ratingFilter} onValueChange={(v) => handleFilterChange('rating', v)}>
              <SelectTrigger className="w-full lg:w-36" data-testid="rating-filter">
                <Star className="h-4 w-4 mr-2 text-[#E8C547]" />
                <SelectValue placeholder="Rating" />
              </SelectTrigger>
              <SelectContent>
                {RATING_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={jobFilter} onValueChange={(v) => handleFilterChange('job_id', v)}>
              <SelectTrigger className="w-full lg:w-44" data-testid="job-filter">
                <SelectValue placeholder="Job" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Jobs</SelectItem>
                {jobs.map(job => (
                  <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={(v) => handleFilterChange('sort', v)}>
              <SelectTrigger className="w-full lg:w-40" data-testid="sort-select">
                <ArrowUpDown className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={mileageFilter} onValueChange={(v) => handleFilterChange('mileage_flag', v)}>
              <SelectTrigger className="w-full lg:w-52" data-testid="mileage-flag-filter">
                <AlertTriangle className="h-4 w-4 mr-2 text-amber-500" />
                <SelectValue placeholder="Mileage Flag" />
              </SelectTrigger>
              <SelectContent>
                {MILEAGE_FLAG_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Clock className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="text-sm text-yellow-700">Pending</p>
                <p className="text-xl font-bold text-yellow-800">
                  {candidates.filter(c => c.status === 'pending_review').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">Interviewing</p>
                <p className="text-xl font-bold text-blue-800">
                  {candidates.filter(c => ['interview_sent', 'interview_confirmed', 'interviewed'].includes(c.status)).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <UserCheck className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Hired</p>
                <p className="text-xl font-bold text-green-800">
                  {candidates.filter(c => c.status === 'hired').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-gray-600" />
              <div>
                <p className="text-sm text-gray-700">Total</p>
                <p className="text-xl font-bold text-gray-800">{candidates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Candidates Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#4B2D6E] border-t-transparent mx-auto"></div>
            </div>
          ) : filteredCandidates.length === 0 ? (
            <div className="p-12 text-center text-[#666666]">
              <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No candidates found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5F5]">
                    {isOwner && (
                      <TableHead className="w-10">
                        <button onClick={toggleSelectAll} className="p-1" data-testid="select-all-checkbox">
                          {allSelected ? (
                            <CheckSquare className="h-4 w-4 text-[#4B2D6E]" />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </TableHead>
                    )}
                    <TableHead className="font-semibold text-[#4B2D6E]">Candidate</TableHead>
                    <TableHead className="font-semibold text-[#4B2D6E]">Position</TableHead>
                    <TableHead className="font-semibold text-[#4B2D6E]">AI Score</TableHead>
                    <TableHead className="font-semibold text-[#4B2D6E]">
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-[#E8C547]" />
                        My Rating
                      </div>
                    </TableHead>
                    <TableHead className="font-semibold text-[#4B2D6E]">Referrer</TableHead>
                    <TableHead className="font-semibold text-[#4B2D6E]">Status</TableHead>
                    <TableHead className="font-semibold text-[#4B2D6E]">Applied</TableHead>
                    <TableHead className="font-semibold text-[#4B2D6E] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCandidates.map((candidate) => (
                    <TableRow 
                      key={candidate.id} 
                      className={`hover:bg-[#F5F5F5] ${selectedIds.has(candidate.id) ? 'bg-purple-50' : ''}`} 
                      data-testid={`candidate-row-${candidate.id}`}
                    >
                      {isOwner && (
                        <TableCell>
                          <button onClick={() => toggleSelect(candidate.id)} className="p-1" data-testid={`select-${candidate.id}`}>
                            {selectedIds.has(candidate.id) ? (
                              <CheckSquare className="h-4 w-4 text-[#4B2D6E]" />
                            ) : (
                              <Square className="h-4 w-4 text-gray-400" />
                            )}
                          </button>
                        </TableCell>
                      )}
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#333333]">
                              {candidate.contact_info?.first_name 
                                ? `${candidate.contact_info.first_name} ${candidate.contact_info.last_name}`
                                : (candidate.parsed_resume?.name && candidate.parsed_resume?.name !== 'N/A' 
                                  ? candidate.parsed_resume.name 
                                  : 'Unknown')}
                            </p>
                            {candidate.mileage_flag && (
                              <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-xs" data-testid={`mileage-flag-${candidate.id}`}>
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                {candidate.mileage_flag === 'concern' ? 'Mileage Policy Concern' : 'Mileage Policy Question'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-[#666666]">
                            {candidate.contact_info?.email || candidate.parsed_resume?.email || '--'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>{candidate.job_title}</TableCell>
                      <TableCell>{getScoreBadge(candidate.ai_score)}</TableCell>
                      <TableCell>
                        {isOwner ? (
                          <StarRating 
                            rating={candidate.owner_rating || 0} 
                            onChange={(r) => updateRating(candidate.id, r)}
                            size="sm"
                            showLabel={false}
                          />
                        ) : (
                          renderStars(candidate.owner_rating)
                        )}
                      </TableCell>
                      <TableCell>
                        {candidate.referrer ? (
                          <Badge variant="outline" className="border-[#4B2D6E] text-[#4B2D6E]">
                            {candidate.referrer.name}
                          </Badge>
                        ) : (
                          <span className="text-[#666666]">Direct</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className={STATUS_COLORS[candidate.status] || 'bg-gray-100'}>
                          {candidate.status?.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#666666]">
                        {new Date(candidate.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/dashboard/candidates/${candidate.id}`}>
                            <Button variant="ghost" size="sm" data-testid={`view-btn-${candidate.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                          {isOwner && candidate.status === 'pending_review' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => setApproveCandidate(candidate)}
                                data-testid={`approve-btn-${candidate.id}`}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => updateStatus(candidate.id, 'declined')}
                                data-testid={`decline-btn-${candidate.id}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>

      {/* Approval Confirmation Dialog */}
      <AlertDialog open={!!approveCandidate} onOpenChange={(open) => { if (!open) setApproveCandidate(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Candidate Approval</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to approve <strong>{approveCandidate?.parsed_resume?.name || 'this candidate'}</strong>. 
              This will notify them via SMS/email that they've moved forward in the hiring process. 
              You can undo this action later from their profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-approve-dialog">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                updateStatus(approveCandidate.id, 'approved');
                setApproveCandidate(null);
              }}
              data-testid="confirm-approve-dialog"
            >
              Yes, Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default CandidatesPage;
